import React, { useState, useId } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Book, Database, Users, Code, Server, Shield, Zap, Package, TrendingUp, FileText, CheckCircle, Copy, ExternalLink, ChevronDown, ChevronRight, Home } from 'lucide-react';
interface DocumentationProps {
  onNavigate: (path: string) => void;
}
export function Documentation({
  onNavigate
}: DocumentationProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>('overview');
  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-violet-50 py-8 px-4" data-id="element-1529">
      <div className="max-w-6xl mx-auto" data-id="element-1530">
        {/* Header */}
        <div className="mb-8" data-id="element-1531">
          <Button variant="outline" size="sm" icon={<Home className="w-4 h-4" data-id="element-1533" />} onClick={() => onNavigate('/login')} className="mb-4" data-id="element-1532">
            Kembali ke Login
          </Button>

          <div className="flex items-center gap-4 mb-4" data-id="element-1534">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-violet-600 rounded-2xl flex items-center justify-center shadow-2xl" data-id="element-1535">
              <Book className="w-8 h-8 text-white" data-id="element-1536" />
            </div>
            <div data-id="element-1537">
              <h1 className="text-4xl font-extrabold text-slate-900" data-id="element-1538">
                System Documentation
              </h1>
              <p className="text-lg text-slate-600 mt-1" data-id="element-1539">
                KDMP B2B Procurement & Supply Chain Platform
              </p>
            </div>
          </div>

          <div className="flex gap-2" data-id="element-1540">
            <Badge variant="success" data-id="element-1541">v1.0.0</Badge>
            <Badge variant="neutral" data-id="element-1542">Production Ready</Badge>
            <Badge variant="warning" data-id="element-1543">33 Pages</Badge>
            <Badge variant="neutral" data-id="element-1544">6 Roles</Badge>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8" data-id="element-1545">
          <Card className="p-4 bg-blue-50 border-blue-200" data-id="element-1546">
            <div className="text-center" data-id="element-1547">
              <div className="text-3xl font-bold text-blue-900 mb-1" data-id="element-1548">6</div>
              <div className="text-xs text-blue-700" data-id="element-1549">User Roles</div>
            </div>
          </Card>
          <Card className="p-4 bg-green-50 border-green-200" data-id="element-1550">
            <div className="text-center" data-id="element-1551">
              <div className="text-3xl font-bold text-green-900 mb-1" data-id="element-1552">33</div>
              <div className="text-xs text-green-700" data-id="element-1553">Total Pages</div>
            </div>
          </Card>
          <Card className="p-4 bg-violet-50 border-violet-200" data-id="element-1554">
            <div className="text-center" data-id="element-1555">
              <div className="text-3xl font-bold text-violet-900 mb-1" data-id="element-1556">8</div>
              <div className="text-xs text-violet-700" data-id="element-1557">Data Entities</div>
            </div>
          </Card>
          <Card className="p-4 bg-amber-50 border-amber-200" data-id="element-1558">
            <div className="text-center" data-id="element-1559">
              <div className="text-3xl font-bold text-amber-900 mb-1" data-id="element-1560">100%</div>
              <div className="text-xs text-amber-700" data-id="element-1561">Complete</div>
            </div>
          </Card>
        </div>

        {/* Documentation Sections */}
        <div className="space-y-4" data-id="element-1562">
          {/* System Overview */}
          <Card className="overflow-hidden" data-id="element-1563">
            <button onClick={() => toggleSection('overview')} className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-1564">
              <div className="flex items-center gap-3" data-id="element-1565">
                <Zap className="w-6 h-6 text-blue-600" data-id="element-1566" />
                <h2 className="text-xl font-bold text-slate-900" data-id="element-1567">
                  System Overview
                </h2>
              </div>
              {expandedSection === 'overview' ? <ChevronDown className="w-5 h-5" data-id="element-1568" /> : <ChevronRight className="w-5 h-5" data-id="element-1569" />}
            </button>

            {expandedSection === 'overview' && <div className="px-6 pb-6 space-y-4 border-t border-slate-200" data-id="element-1570">
                <div className="prose prose-sm max-w-none" data-id="element-1571">
                  <h3 className="text-lg font-bold text-slate-900 mt-4" data-id="element-1572">
                    Platform Architecture
                  </h3>
                  <p className="text-slate-700" data-id="element-1573">
                    KDMP B2B Platform adalah sistem procurement dan supply chain
                    management yang menghubungkan Koperasi (Buyer), Agregator
                    (PTX), Factory, Warehouse, dan Logistics Partner dalam satu
                    ekosistem terintegrasi.
                  </p>

                  <h4 className="text-base font-bold text-slate-900 mt-4" data-id="element-1574">
                    Technology Stack
                  </h4>
                  <ul className="list-disc pl-5 space-y-1 text-slate-700" data-id="element-1575">
                    <li data-id="element-1576">
                      <strong data-id="element-1577">Frontend:</strong> React 18 + TypeScript +
                      Tailwind CSS
                    </li>
                    <li data-id="element-1578">
                      <strong data-id="element-1579">State Management:</strong> Centralized data store
                      (sharedData.ts)
                    </li>
                    <li data-id="element-1580">
                      <strong data-id="element-1581">Routing:</strong> Client-side routing with History
                      API
                    </li>
                    <li data-id="element-1582">
                      <strong data-id="element-1583">Icons:</strong> Lucide React
                    </li>
                    <li data-id="element-1584">
                      <strong data-id="element-1585">Build Tool:</strong> Vite
                    </li>
                  </ul>

                  <h4 className="text-base font-bold text-slate-900 mt-4" data-id="element-1586">
                    Key Features
                  </h4>
                  <div className="grid grid-cols-2 gap-3" data-id="element-1587">
                    <div className="flex items-start gap-2" data-id="element-1588">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1589" />
                      <span className="text-sm text-slate-700" data-id="element-1590">
                        6 role-based portals
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1591">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1592" />
                      <span className="text-sm text-slate-700" data-id="element-1593">
                        Complete supply chain flow
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1594">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1595" />
                      <span className="text-sm text-slate-700" data-id="element-1596">
                        Revenue sharing model
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1597">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1598" />
                      <span className="text-sm text-slate-700" data-id="element-1599">
                        Real-time tracking
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1600">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1601" />
                      <span className="text-sm text-slate-700" data-id="element-1602">
                        Fully responsive design
                      </span>
                    </div>
                    <div className="flex items-start gap-2" data-id="element-1603">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" data-id="element-1604" />
                      <span className="text-sm text-slate-700" data-id="element-1605">
                        Mobile menu support
                      </span>
                    </div>
                  </div>
                </div>
              </div>}
          </Card>

          {/* User Roles */}
          <Card className="overflow-hidden" data-id="element-1606">
            <button onClick={() => toggleSection('roles')} className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-1607">
              <div className="flex items-center gap-3" data-id="element-1608">
                <Users className="w-6 h-6 text-green-600" data-id="element-1609" />
                <h2 className="text-xl font-bold text-slate-900" data-id="element-1610">
                  User Roles & Credentials
                </h2>
              </div>
              {expandedSection === 'roles' ? <ChevronDown className="w-5 h-5" data-id="element-1611" /> : <ChevronRight className="w-5 h-5" data-id="element-1612" />}
            </button>

            {expandedSection === 'roles' && <div className="px-6 pb-6 border-t border-slate-200" data-id="element-1613">
                <div className="grid grid-cols-2 gap-4 mt-4" data-id="element-1614">
                  {[{
                role: 'Buyer',
                email: 'buyer@majujaya.coop',
                user: 'Budi Santoso',
                pages: 8,
                color: 'blue'
              }, {
                role: 'Admin (PTX)',
                email: 'admin@ptx.co.id',
                user: 'Admin Operasional',
                pages: 9,
                color: 'slate'
              }, {
                role: 'Super Admin',
                email: 'owner@kdmp.com',
                user: 'Platform Owner',
                pages: 4,
                color: 'violet'
              }, {
                role: 'Logistics',
                email: 'partner@jne.co.id',
                user: 'JNE Operations',
                pages: 4,
                color: 'green'
              }, {
                role: 'Factory',
                email: 'po@indofood.co.id',
                user: 'Factory Manager',
                pages: 4,
                color: 'orange'
              }, {
                role: 'Warehouse',
                email: 'bogor@ptx-warehouse.co.id',
                user: 'Warehouse Manager',
                pages: 4,
                color: 'cyan'
              }].map((item, i) => <div key={i} className={`p-4 bg-${item.color}-50 border border-${item.color}-200 rounded-lg`} data-id="element-1615">
                      <div className="flex items-center justify-between mb-2" data-id="element-1616">
                        <h4 className="font-bold text-slate-900" data-id="element-1617">
                          {item.role}
                        </h4>
                        <Badge variant="neutral" className="text-xs" data-id="element-1618">
                          {item.pages} pages
                        </Badge>
                      </div>
                      <div className="space-y-1 text-xs" data-id="element-1619">
                        <div className="flex items-center gap-2" data-id="element-1620">
                          <span className="text-slate-600" data-id="element-1621">Email:</span>
                          <code className="bg-white px-2 py-0.5 rounded text-slate-900 font-mono" data-id="element-1622">
                            {item.email}
                          </code>
                          <button onClick={() => copyToClipboard(item.email)} className="text-blue-600 hover:text-blue-700" data-id="element-1623">
                            <Copy className="w-3 h-3" data-id="element-1624" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1625">
                          <span className="text-slate-600" data-id="element-1626">Password:</span>
                          <code className="bg-white px-2 py-0.5 rounded text-slate-900 font-mono" data-id="element-1627">
                            demo123
                          </code>
                          <button onClick={() => copyToClipboard('demo123')} className="text-blue-600 hover:text-blue-700" data-id="element-1628">
                            <Copy className="w-3 h-3" data-id="element-1629" />
                          </button>
                        </div>
                        <div className="text-slate-600" data-id="element-1630">
                          User:{' '}
                          <span className="text-slate-900 font-medium" data-id="element-1631">
                            {item.user}
                          </span>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>}
          </Card>

          {/* Database Structure */}
          <Card className="overflow-hidden" data-id="element-1632">
            <button onClick={() => toggleSection('database')} className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-1633">
              <div className="flex items-center gap-3" data-id="element-1634">
                <Database className="w-6 h-6 text-violet-600" data-id="element-1635" />
                <h2 className="text-xl font-bold text-slate-900" data-id="element-1636">
                  Database Structure
                </h2>
              </div>
              {expandedSection === 'database' ? <ChevronDown className="w-5 h-5" data-id="element-1637" /> : <ChevronRight className="w-5 h-5" data-id="element-1638" />}
            </button>

            {expandedSection === 'database' && <div className="px-6 pb-6 border-t border-slate-200" data-id="element-1639">
                <div className="mt-4 space-y-4" data-id="element-1640">
                  <h4 className="font-bold text-slate-900" data-id="element-1641">Core Entities</h4>

                  {[{
                name: 'Cooperatives',
                fields: ['id', 'name', 'location', 'chairman', 'creditLimit', 'creditUsed', 'warehouseId'],
                count: 2
              }, {
                name: 'Orders',
                fields: ['id', 'cooperativeId', 'warehouseId', 'logisticsId', 'factoryId', 'items[]', 'status', 'totalValue'],
                count: 3
              }, {
                name: 'Products',
                fields: ['id', 'sku', 'name', 'category', 'supplierId', 'moq', 'basePrice', 'sellingPrice', 'stock'],
                count: 4
              }, {
                name: 'Logistics Partners',
                fields: ['id', 'name', 'totalShipments', 'successRate', 'avgDeliveryTime', 'revenue'],
                count: 2
              }, {
                name: 'Factories',
                fields: ['id', 'name', 'category', 'totalPOs', 'onTimeRate', 'qualityRate', 'capacityUtilization'],
                count: 2
              }, {
                name: 'Warehouses',
                fields: ['id', 'name', 'location', 'capacity', 'currentStock', 'utilizationRate'],
                count: 2
              }].map((entity, i) => <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-1642">
                      <div className="flex items-center justify-between mb-2" data-id="element-1643">
                        <h5 className="font-bold text-slate-900" data-id="element-1644">
                          {entity.name}
                        </h5>
                        <Badge variant="neutral" className="text-xs" data-id="element-1645">
                          {entity.count} records
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1" data-id="element-1646">
                        {entity.fields.map((field, j) => <code key={j} className="text-xs bg-white px-2 py-1 rounded border border-slate-200 text-slate-700" data-id="element-1647">
                            {field}
                          </code>)}
                      </div>
                    </div>)}

                  <h4 className="font-bold text-slate-900 mt-6" data-id="element-1648">
                    Relationships
                  </h4>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 font-mono text-xs space-y-1 text-slate-700" data-id="element-1649">
                    <div data-id="element-1650">Order → Cooperative (cooperativeId)</div>
                    <div data-id="element-1651">Order → Warehouse (warehouseId)</div>
                    <div data-id="element-1652">Order → Factory (factoryId)</div>
                    <div data-id="element-1653">Order → Logistics (logisticsId)</div>
                    <div data-id="element-1654">Product → Factory (supplierId)</div>
                    <div data-id="element-1655">Cooperative → Warehouse (warehouseId)</div>
                  </div>
                </div>
              </div>}
          </Card>

          {/* API Endpoints */}
          <Card className="overflow-hidden" data-id="element-1656">
            <button onClick={() => toggleSection('api')} className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-1657">
              <div className="flex items-center gap-3" data-id="element-1658">
                <Server className="w-6 h-6 text-amber-600" data-id="element-1659" />
                <h2 className="text-xl font-bold text-slate-900" data-id="element-1660">
                  API Endpoints (Backend Integration)
                </h2>
              </div>
              {expandedSection === 'api' ? <ChevronDown className="w-5 h-5" data-id="element-1661" /> : <ChevronRight className="w-5 h-5" data-id="element-1662" />}
            </button>

            {expandedSection === 'api' && <div className="px-6 pb-6 border-t border-slate-200" data-id="element-1663">
                <div className="mt-4 space-y-3" data-id="element-1664">
                  {[{
                method: 'POST',
                endpoint: '/api/auth/login',
                desc: 'User authentication'
              }, {
                method: 'POST',
                endpoint: '/api/auth/logout',
                desc: 'User logout'
              }, {
                method: 'GET',
                endpoint: '/api/auth/me',
                desc: 'Get current user'
              }, {
                method: 'GET',
                endpoint: '/api/orders',
                desc: 'Get all orders'
              }, {
                method: 'POST',
                endpoint: '/api/orders',
                desc: 'Create new order'
              }, {
                method: 'GET',
                endpoint: '/api/orders/:id',
                desc: 'Get order by ID'
              }, {
                method: 'PUT',
                endpoint: '/api/orders/:id/status',
                desc: 'Update order status'
              }, {
                method: 'GET',
                endpoint: '/api/products',
                desc: 'Get all products'
              }, {
                method: 'POST',
                endpoint: '/api/products',
                desc: 'Create product'
              }, {
                method: 'PUT',
                endpoint: '/api/products/:id',
                desc: 'Update product'
              }, {
                method: 'GET',
                endpoint: '/api/tracking/:orderId',
                desc: 'Get tracking info'
              }, {
                method: 'GET',
                endpoint: '/api/revenue/analytics',
                desc: 'Revenue analytics'
              }, {
                method: 'GET',
                endpoint: '/api/system/health',
                desc: 'System health check'
              }].map((api, i) => <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-id="element-1665">
                      <Badge variant={api.method === 'GET' ? 'success' : api.method === 'POST' ? 'warning' : 'neutral'} className="font-mono text-xs" data-id="element-1666">
                        {api.method}
                      </Badge>
                      <code className="flex-1 text-sm text-slate-900 font-mono" data-id="element-1667">
                        {api.endpoint}
                      </code>
                      <span className="text-xs text-slate-600" data-id="element-1668">{api.desc}</span>
                    </div>)}
                </div>
              </div>}
          </Card>

          {/* Revenue Model */}
          <Card className="overflow-hidden" data-id="element-1669">
            <button onClick={() => toggleSection('revenue')} className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-1670">
              <div className="flex items-center gap-3" data-id="element-1671">
                <TrendingUp className="w-6 h-6 text-green-600" data-id="element-1672" />
                <h2 className="text-xl font-bold text-slate-900" data-id="element-1673">
                  Revenue Sharing Model
                </h2>
              </div>
              {expandedSection === 'revenue' ? <ChevronDown className="w-5 h-5" data-id="element-1674" /> : <ChevronRight className="w-5 h-5" data-id="element-1675" />}
            </button>

            {expandedSection === 'revenue' && <div className="px-6 pb-6 border-t border-slate-200" data-id="element-1676">
                <div className="mt-4 grid grid-cols-2 gap-4" data-id="element-1677">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1678">
                    <h4 className="font-bold text-blue-900 mb-3" data-id="element-1679">
                      Platform Revenue (47%)
                    </h4>
                    <div className="space-y-2 text-sm" data-id="element-1680">
                      <div className="flex justify-between" data-id="element-1681">
                        <span className="text-blue-700" data-id="element-1682">Subscription Fee:</span>
                        <span className="font-bold text-blue-900" data-id="element-1683">
                          Rp 25.0Jt (100%)
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-1684">
                        <span className="text-blue-700" data-id="element-1685">Service Fee:</span>
                        <span className="font-bold text-blue-900" data-id="element-1686">
                          Rp 16.8Jt (40%)
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-1687">
                        <span className="text-blue-700" data-id="element-1688">Commission:</span>
                        <span className="font-bold text-blue-900" data-id="element-1689">
                          Rp 5.4Jt (30%)
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-1690">
                        <span className="text-blue-700" data-id="element-1691">Logistics Margin:</span>
                        <span className="font-bold text-blue-900" data-id="element-1692">
                          Rp 12.0Jt (50%)
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-blue-300" data-id="element-1693">
                        <span className="text-blue-700 font-bold" data-id="element-1694">Total:</span>
                        <span className="font-bold text-blue-900" data-id="element-1695">
                          Rp 59.2Jt
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg" data-id="element-1696">
                    <h4 className="font-bold text-green-900 mb-3" data-id="element-1697">
                      PTX Revenue (53%)
                    </h4>
                    <div className="space-y-2 text-sm" data-id="element-1698">
                      <div className="flex justify-between" data-id="element-1699">
                        <span className="text-green-700" data-id="element-1700">Service Fee:</span>
                        <span className="font-bold text-green-900" data-id="element-1701">
                          Rp 25.2Jt (60%)
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-1702">
                        <span className="text-green-700" data-id="element-1703">Commission:</span>
                        <span className="font-bold text-green-900" data-id="element-1704">
                          Rp 12.6Jt (70%)
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-1705">
                        <span className="text-green-700" data-id="element-1706">
                          Logistics Margin:
                        </span>
                        <span className="font-bold text-green-900" data-id="element-1707">
                          Rp 12.0Jt (50%)
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-1708">
                        <span className="text-green-700" data-id="element-1709">Product Margin:</span>
                        <span className="font-bold text-green-900" data-id="element-1710">
                          Rp 15.8Jt (100%)
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-green-300" data-id="element-1711">
                        <span className="text-green-700 font-bold" data-id="element-1712">Total:</span>
                        <span className="font-bold text-green-900" data-id="element-1713">
                          Rp 65.6Jt
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-violet-50 border border-violet-200 rounded-lg" data-id="element-1714">
                  <div className="flex items-center justify-between" data-id="element-1715">
                    <div data-id="element-1716">
                      <h4 className="font-bold text-violet-900" data-id="element-1717">
                        Total Platform Revenue
                      </h4>
                      <p className="text-sm text-violet-700" data-id="element-1718">
                        Monthly GMV: Rp 1.2M
                      </p>
                    </div>
                    <div className="text-right" data-id="element-1719">
                      <div className="text-3xl font-bold text-violet-900" data-id="element-1720">
                        Rp 124.8Jt
                      </div>
                      <div className="text-sm text-violet-700" data-id="element-1721">per month</div>
                    </div>
                  </div>
                </div>
              </div>}
          </Card>

          {/* Deployment Guide */}
          <Card className="overflow-hidden" data-id="element-1722">
            <button onClick={() => toggleSection('deployment')} className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-1723">
              <div className="flex items-center gap-3" data-id="element-1724">
                <Package className="w-6 h-6 text-blue-600" data-id="element-1725" />
                <h2 className="text-xl font-bold text-slate-900" data-id="element-1726">
                  Deployment Guide
                </h2>
              </div>
              {expandedSection === 'deployment' ? <ChevronDown className="w-5 h-5" data-id="element-1727" /> : <ChevronRight className="w-5 h-5" data-id="element-1728" />}
            </button>

            {expandedSection === 'deployment' && <div className="px-6 pb-6 border-t border-slate-200" data-id="element-1729">
                <div className="mt-4 space-y-4" data-id="element-1730">
                  <div data-id="element-1731">
                    <h4 className="font-bold text-slate-900 mb-2" data-id="element-1732">
                      Installation
                    </h4>
                    <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-sm" data-id="element-1733">
                      <div data-id="element-1734">$ npm install</div>
                      <div data-id="element-1735">$ npm run dev</div>
                    </div>
                  </div>

                  <div data-id="element-1736">
                    <h4 className="font-bold text-slate-900 mb-2" data-id="element-1737">
                      Build for Production
                    </h4>
                    <div className="bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-sm" data-id="element-1738">
                      <div data-id="element-1739">$ npm run build</div>
                      <div data-id="element-1740">$ npm run preview</div>
                    </div>
                  </div>

                  <div data-id="element-1741">
                    <h4 className="font-bold text-slate-900 mb-2" data-id="element-1742">
                      Environment Variables
                    </h4>
                    <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 font-mono text-sm space-y-1" data-id="element-1743">
                      <div className="text-slate-700" data-id="element-1744">
                        VITE_API_URL=https://api.kdmp.com
                      </div>
                      <div className="text-slate-700" data-id="element-1745">
                        VITE_WS_URL=wss://ws.kdmp.com
                      </div>
                      <div className="text-slate-700" data-id="element-1746">
                        VITE_CDN_URL=https://cdn.kdmp.com
                      </div>
                    </div>
                  </div>

                  <div data-id="element-1747">
                    <h4 className="font-bold text-slate-900 mb-2" data-id="element-1748">
                      Deployment Checklist
                    </h4>
                    <div className="space-y-2" data-id="element-1749">
                      {['Setup backend API endpoints', 'Configure authentication (JWT)', 'Setup database (PostgreSQL/MongoDB)', 'Configure CDN for assets', 'Setup SSL certificates', 'Configure monitoring (Sentry)', 'Setup CI/CD pipeline', 'Performance testing', 'Security audit', 'Go live!'].map((item, i) => <div key={i} className="flex items-center gap-2 text-sm" data-id="element-1750">
                          <CheckCircle className="w-4 h-4 text-green-600" data-id="element-1751" />
                          <span className="text-slate-700" data-id="element-1752">{item}</span>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>}
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center" data-id="element-1753">
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-violet-50 border-blue-200" data-id="element-1754">
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-1755">Need Help?</h3>
            <p className="text-sm text-slate-600 mb-4" data-id="element-1756">
              For technical support or questions about the system, please
              contact the development team.
            </p>
            <div className="flex gap-2 justify-center" data-id="element-1757">
              <Button variant="primary" size="sm" icon={<FileText className="w-4 h-4" data-id="element-1759" />} data-id="element-1758">
                Download PDF
              </Button>
              <Button variant="outline" size="sm" icon={<ExternalLink className="w-4 h-4" data-id="element-1761" />} data-id="element-1760">
                API Reference
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>;
}